import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import JiraKeyChip from '../JiraKeyChip';
import { apiService } from '../../services/api';
import { SearchData } from './types';
import { personPath } from './sentences';
import { CARD, ROW_LINK } from './Block';

const MIN_CHARS = 2;
const WAIT_MS = 250;

// "Find a ticket, person or project": searches only what the signed-in person could already open.
// A leader cannot open Worklogs by person, so a person opens in My people (their teams' people); everyone else opens the person's month.
export default function HomeSearch({ projectLink, personLink = personPath }: { projectLink: (key: string) => string; personLink?: (accountId: string) => string }) {
  const inputId = useId();
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<SearchData | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_CHARS) {
      setFound(null);
      setFailed(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      apiService
        .searchPortal(q, controller.signal)
        .then((r) => {
          if (controller.signal.aborted) return;
          setFailed(!r.success);
          setFound(r.success ? r.data : null);
        })
        .catch(() => !controller.signal.aborted && setFailed(true));
    }, WAIT_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term]);

  const empty = found && !found.tickets.length && !found.people.length && !found.projects.length;

  return (
    <div className={`${CARD} p-4`}>
      <label htmlFor={inputId} className="sr-only">
        Find a ticket, person or project
      </label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
        <input
          id={inputId}
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Find a ticket, person or project"
          autoComplete="off"
          className="w-full rounded-xl border border-gray-200 py-2.5 pl-9 pr-3 text-base text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div aria-live="polite">
        {failed && <p className="mt-3 text-sm text-red-700">Search did not work just now. Try again in a moment.</p>}
        {empty && <p className="mt-3 text-sm text-gray-600">Nothing matches that. Try a ticket key, a name or part of a title.</p>}
        {found && !empty && (
          <div className="mt-3 grid gap-4 sm:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]">
            <Group title="Tickets" show={found.tickets.length > 0}>
              {found.tickets.map((t) => (
                <li key={t.issue_key} className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-0.5 py-1">
                  <span className="shrink-0 whitespace-nowrap">
                    <JiraKeyChip issueKey={t.issue_key} url={t.jira_url} />
                  </span>
                  <span className="min-w-0 grow basis-40 break-words text-sm text-gray-800">{t.issue_name ?? t.issue_key}</span>
                </li>
              ))}
            </Group>
            <Group title="People" show={found.people.length > 0}>
              {found.people.map((p) => (
                <li key={p.account_id} className="py-1">
                  <Link to={personLink(p.account_id)} className={ROW_LINK}>
                    {p.display_name ?? p.account_id}
                  </Link>
                </li>
              ))}
            </Group>
            <Group title="Projects" show={found.projects.length > 0}>
              {found.projects.map((p) => (
                <li key={p.project_key} className="py-1">
                  <Link to={projectLink(p.project_key)} className={ROW_LINK}>
                    {p.name ?? p.project_key} ({p.project_key})
                  </Link>
                </li>
              ))}
            </Group>
          </div>
        )}
      </div>
    </div>
  );
}

function Group({ title, show, children }: { title: string; show: boolean; children: React.ReactNode }) {
  if (!show) return null;
  return (
    <div className="min-w-0">
      <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      <ul>{children}</ul>
    </div>
  );
}
